import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { CartDetailsComponent } from '@spartacus/cart/base/components';
import { CartConfigService, SelectiveCartService } from '@spartacus/cart/base/core';
import { ActiveCartFacade, Cart, OrderEntry, PromotionLocation, PromotionResult } from '@spartacus/cart/base/root';
import { AuthService, BaseSiteService, CmsService, FeatureConfigService, RoutingService } from '@spartacus/core';
import { Observable, Unsubscribable, combineLatest } from 'rxjs';
import { filter, map, take, tap } from 'rxjs/operators';
import { BaseStoreService } from 'src/app/service/base-store/base-store.service';
import { CartExtendService } from 'src/app/service/cart/cart-extend.service';
import { ComponentService } from 'src/app/service/component.service';
import { customDeviceStylings } from 'src/app/service/custom-device-stylings/custom-device-stylings.service';
import { CustomProductService } from 'src/app/service/product/custom-product.service';
import { SnahCountryService, WAREHOUSE } from 'src/app/service/snah/snah-countries.service';
import { StorageService } from 'src/app/service/storage/storage.service';

@Component({
  selector: 'cx-cart-details',
  templateUrl: './cart-details.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ShimanoCartDetailsComponent extends CartDetailsComponent implements OnInit, OnDestroy {
  cart$: Observable<Cart>;
  entries$: Observable<OrderEntry[]>;
  cartLoaded$: Observable<boolean>;
  loggedIn = false;
  orderPromotions$: Observable<PromotionResult[]>;
  promotionLocation: PromotionLocation = PromotionLocation.ActiveCart;
  promotions$: Observable<PromotionResult[]>;
  currentPageHandler: Unsubscribable;
  pageId: string;
  pagetemplate: string;
  defaultCartError = false;
  noStockError = false;
  lowStockError = false;
  nostockErrorEntries: [];
  lowstockErrorEntries: [];
  unapprovedError = false;
  dateFormatted = '';
  baseSite$: Observable<string> = this.baseService.getActive();
  skipVariantCategory =
    this.storageService.getSessionStorageItem('skipVariantCategoryCH') !== null
      ? this.storageService.getSessionStorageItem('skipVariantCategoryCH')
      : '';
  default = 'default';
  cartPageId = 'CartPageTemplate';
  updatedCartEntries: any = [];
  appliedPromotions: any = [];

  showActiveSnahCountry$: Observable<boolean> = this.snahCountryService.getSnahFeatures(WAREHOUSE);

  constructor(
    protected activeCartService: ActiveCartFacade,
    protected selectiveCartService: SelectiveCartService,
    protected authService: AuthService,
    protected routingService: RoutingService,
    private snahCountryService: SnahCountryService,
    private storageService: StorageService,
    protected cms?: CmsService,
    protected cartConfigService?: CartConfigService,
    private featureConfig?: FeatureConfigService,
    private baseStoreService?: BaseStoreService,
    private baseService?: BaseSiteService,
    private customProductService?: CustomProductService,
    private cartExtendService?: CartExtendService,
    private cdr?: ChangeDetectorRef,
    private componentService?: ComponentService,
    private deviceStylings?: customDeviceStylings
  ) {
    super(activeCartService, selectiveCartService, authService, routingService, cartConfigService);

    this.currentPageHandler = this.cms.getCurrentPage().subscribe((str) => {
      if (typeof str !== 'undefined') {
        this.pageId = str.pageId;
        this.pagetemplate = str.template;
      }
    });

    this.baseStoreService
      .getBasestoreData()
      .pipe(take(1))
      .subscribe((baseStoreData) => {
        this.dateFormatted = baseStoreData.dateFormat.replace(/\//g, '-');
      });
    this.customProductService
      .getSkipVariant()
      .pipe(take(1))
      .subscribe((resp) => {
        this.skipVariantCategory = resp[this.baseSiteId]
          ? resp[this.baseSiteId].defaultVariantCategory
          : resp[this.default].defaultVariantCategory;

        this.storageService.setSessionStorageItem('skipVariantCategoryCH', this.skipVariantCategory);
      });
    this.activeCartService.reloadActiveCart();
  }

  get baseSiteId(): string {
    let baseSiteId: string;
    this.baseSite$.pipe(take(1)).subscribe((baseSiteData) => (baseSiteId = baseSiteData));
    return baseSiteId;
  }

  ngOnInit() {
    this.cart$ = this.activeCartService.getActive();
    this.entries$ = this.activeCartService.getEntries().pipe(
      filter((entries) => entries.length > 0),
      tap((entries) => {
        this.updatedCartEntries.push([...entries]);
        this.componentService.cartEntries$.next(this.updatedCartEntries);
        this.getAppliedPromotions();
      })
    );

    if (this.isSaveForLaterEnabled()) {
      this.cartLoaded$ = combineLatest([this.activeCartService.isStable(), this.authService.isUserLoggedIn()]).pipe(
        tap(([, loggedIn]) => (this.loggedIn = loggedIn)),
        map(([cartLoaded, loggedIn]) => (loggedIn ? cartLoaded : cartLoaded))
      );
    } else {
      this.cartLoaded$ = this.activeCartService.isStable();
    }

    // Check if theres error during cart validation.
    if (this.storageService.getSessionStorageItem('defaultCartError') !== null) {
      this.defaultCartError = true;
      this.storageService.removeSessionStorageItem('defaultCartError');
    }

    if (this.storageService.getSessionStorageItem('noStockError') !== null) {
      this.noStockError = true;
      this.nostockErrorEntries = JSON.parse(this.storageService.getSessionStorageItem('noStockError'));
      this.storageService.removeSessionStorageItem('noStockError');
    }

    if (this.storageService.getSessionStorageItem('lowStockError') !== null) {
      this.lowStockError = true;
      this.lowstockErrorEntries = JSON.parse(this.storageService.getSessionStorageItem('lowStockError'));
      this.storageService.removeSessionStorageItem('lowStockError');
    }
    if (this.storageService.getSessionStorageItem('unapprovedError') !== null) {
      this.unapprovedError = true;
      this.storageService.removeSessionStorageItem('unapprovedError');
    }

    this.storageService.removeSessionStorageItem('MOQCartError');
    this.tabletCustomSylings();
  }

  //TODO remove feature flag for #5958
  isSaveForLaterEnabled(): boolean {
    if (this.featureConfig) {
      return this.featureConfig.isEnabled('saveForLater');
    }
    return false;
  }

  @HostListener('window:resize')
  onResize(): void {
    setTimeout(() => {
      this.tabletCustomSylings();
      this.cdr.detectChanges();
    }, 500);
  }

  tabletCustomSylings() {
    const elCartSummary = document.querySelector('.CenterLeftContentSlot');
    if (this.deviceStylings.isTabletMaxWidthExceeds()) {
      elCartSummary?.classList?.add('adjustedStylings');
    } else if (!this.deviceStylings.isTabletMaxWidthExceeds()) {
      elCartSummary?.classList.remove('adjustedStylings');
    }
  }

  getAppliedPromotions() {
    this.cart$.pipe(take(1)).subscribe((data: any) => {
      if (data.appliedProductPromotions) {
        this.appliedPromotions = data.appliedProductPromotions;
        this.cdr.detectChanges();
      }
    });
  }
  //TODO remove feature flag for #5958 end

  getAllPromotionsForCart(cart: Cart): any[] {
    const potentialPromotions = [];
    potentialPromotions.push(...(cart.potentialOrderPromotions || []));
    potentialPromotions.push(...(cart.potentialProductPromotions || []));

    const appliedPromotions = [];
    appliedPromotions.push(...(cart.appliedOrderPromotions || []));
    appliedPromotions.push(...(cart.appliedProductPromotions || []));

    return [...potentialPromotions, ...appliedPromotions];
  }

  saveForLater(item: OrderEntry) {
    if (this.loggedIn) {
      this.activeCartService.removeEntry(item);
    } else {
      this.routingService.go({ cxRoute: 'login' });
    }
  }
  ngOnDestroy() {
    this.currentPageHandler.unsubscribe();
    this.storageService.removeSessionStorageItem('MOQCartError');
  }
}
